import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from '../components/Logo';
import {
  Users,
  BriefcaseBusiness,
  Headphones,
  CreditCard,
  ShieldCheck,
  Sparkles,
  KeyRound,
  Activity,
  CheckCircle2,
  Clock,
  TrendingUp,
  Layers,
} from 'lucide-react';

/**
 * Hubs workflow data for the live animated command center showcase.
 * Visually illustrates "Multiple work done in a single place".
 */
const WORKSPACE_HUBS = [
  {
    id: 'work',
    tabName: 'Work & Delivery',
    hubName: 'Agile Projects & Gemini AI',
    icon: BriefcaseBusiness,
    themeColor: 'text-sidebar-active',
    iconBox: 'bg-sidebar-active/15 text-sidebar-active border-sidebar-active/20',
    badge: 'Sprint 14 • 90% Completed',
    badgeColor: 'text-sidebar-active bg-sidebar-active/10 border-sidebar-active/20',
    progress: 90,
    items: [
      {
        title: 'Microservices Gateway v1.0',
        meta: 'PR merged to main • CI/CD deployed',
        status: 'Done',
        statusColor: 'text-success bg-success/10 border-success/20',
      },
      {
        title: 'Gemini AI Retrospective',
        meta: 'Sprint summary auto-generated for team',
        status: 'Synced',
        statusColor: 'text-sidebar-active bg-sidebar-active/10 border-sidebar-active/20',
      },
      {
        title: 'Developer Timesheets',
        meta: '38.5 hrs approved across 6 engineers',
        status: 'Approved',
        statusColor: 'text-info bg-info/10 border-info/20',
      },
    ],
    highlight: '14 pull requests and 20 sprint tasks closed',
  },
  {
    id: 'people',
    tabName: 'People & HR',
    hubName: 'Workforce & Attendance',
    icon: Users,
    themeColor: 'text-success',
    iconBox: 'bg-success/15 text-success border-success/20',
    badge: '48/52 In Office Today',
    badgeColor: 'text-success bg-success/10 border-success/20',
    progress: 92,
    items: [
      {
        title: 'Sarah Mitchell (Tech Lead)',
        meta: 'Geo-verified check-in at 09:12 AM',
        status: 'Present',
        statusColor: 'text-success bg-success/10 border-success/20',
      },
      {
        title: 'Automated Monthly Payroll',
        meta: 'Direct deposits queued for 52 accounts',
        status: 'Ready',
        statusColor: 'text-sidebar-active bg-sidebar-active/10 border-sidebar-active/20',
      },
      {
        title: 'Casual Leave Request (3d)',
        meta: 'Manager approval routed automatically',
        status: 'Approved',
        statusColor: 'text-info bg-info/10 border-info/20',
      },
    ],
    highlight: '100% geofenced attendance accuracy',
  },
  {
    id: 'support',
    tabName: 'ITSM & Assets',
    hubName: 'IT Service Desk & Assets',
    icon: Headphones,
    themeColor: 'text-info',
    iconBox: 'bg-info/15 text-info border-info/20',
    badge: '99.98% SLA Met',
    badgeColor: 'text-info bg-info/10 border-info/20',
    progress: 100,
    items: [
      {
        title: 'Ticket #ITSM-284: VPN Latency',
        meta: 'Resolved in 7 mins (Target: 15m)',
        status: 'Resolved',
        statusColor: 'text-success bg-success/10 border-success/20',
      },
      {
        title: 'MacBook Pro M3 Allocation',
        meta: 'Hardware assigned to new engineering hire',
        status: 'Assigned',
        statusColor: 'text-info bg-info/10 border-info/20',
      },
      {
        title: 'SaaS Compliance & License Audit',
        meta: 'Zero unmanaged seats detected across tools',
        status: 'Verified',
        statusColor: 'text-sidebar-active bg-sidebar-active/10 border-sidebar-active/20',
      },
    ],
    highlight: 'Average incident resolution time under 8 mins',
  },
  {
    id: 'money',
    tabName: 'Finance',
    hubName: 'Financial Operations & Billing',
    icon: CreditCard,
    themeColor: 'text-warning',
    iconBox: 'bg-warning/15 text-warning border-warning/20',
    badge: 'Audit & Tax Ready',
    badgeColor: 'text-warning bg-warning/10 border-warning/20',
    progress: 96,
    items: [
      {
        title: 'Client Invoice #TB-2026-92',
        meta: '$18,400 received via Wire Transfer',
        status: 'Settled',
        statusColor: 'text-success bg-success/10 border-success/20',
      },
      {
        title: 'AWS Cloud Infrastructure PO',
        meta: 'Matched with vendor line items',
        status: 'Matched',
        statusColor: 'text-info bg-info/10 border-info/20',
      },
      {
        title: 'Quarterly GST Reconciliation',
        meta: 'Ledger report prepared for audit',
        status: 'Synced',
        statusColor: 'text-warning bg-warning/10 border-warning/20',
      },
    ],
    highlight: 'Zero manual ledger reconciliation required',
  },
];

/**
 * Auth shell.
 * The left brand panel provides a calm, dark navy frame (matching the app sidebar)
 * with an animated live command center proving "multiple work done in a single place".
 */
export function AuthLayout() {
  const [activeTab, setActiveTab] = useState(0);

  // Auto-cycle through the hubs every 4.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % WORKSPACE_HUBS.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [activeTab]);

  const currentHub = WORKSPACE_HUBS[activeTab];
  const HubIcon = currentHub.icon;

  return (
    <div className="min-h-screen bg-background text-foreground md:grid md:grid-cols-[minmax(360px,0.95fr)_minmax(420px,1.05fr)]">
      {/* Dark navy brand panel matching the app sidebar frame */}
      <aside className="relative hidden overflow-hidden bg-sidebar p-8 lg:p-12 text-sidebar-foreground md:flex md:flex-col justify-between border-r border-sidebar-border">
        {/* Top bar with Logo and Live Pulse */}
        <div className="flex items-center justify-between">
          <Logo className="h-9" isDark />
          <div className="inline-flex items-center gap-1.5 rounded-full border border-sidebar-border bg-sidebar-accent/80 px-3 py-1 text-xs text-sidebar-muted font-mono">
            <span className="size-1.5 rounded-full bg-success animate-pulse" />
            <span>4 Hubs Synchronized</span>
          </div>
        </div>

        {/* Center content */}
        <div className="relative my-auto max-w-lg py-6 space-y-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-sidebar-border bg-sidebar-accent/80 px-3 py-1 text-xs font-medium text-sidebar-active">
              <Sparkles className="size-3.5 text-primary" />
              <span>All-In-One Enterprise OS</span>
            </div>

            <h1 className="leading-tight text-white font-display text-page-title sm:text-2xl lg:text-3xl font-semibold tracking-tight">
              The Next-Gen Hub for Unified Business Management.
            </h1>

            <p className="text-sm leading-relaxed text-sidebar-muted">
              Connect people, sprints, IT assets, and finances in one calm, real-time command center.
            </p>
          </div>

          {/* Animated "Multiple Work Done in a Single Place" Card */}
          <div className="rounded-2xl border border-sidebar-border bg-sidebar-accent/40 backdrop-blur-xs p-5 shadow-lg space-y-4">
            {/* Window header with Hub Tabs */}
            <div className="flex items-center justify-between pb-3 border-b border-sidebar-border/70 text-xs text-sidebar-muted">
              <div className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-destructive/70" />
                <span className="size-2 rounded-full bg-warning/70" />
                <span className="size-2 rounded-full bg-success/70" />
                <span className="ml-2 font-mono text-xs uppercase tracking-wider text-sidebar-muted">
                  Live Operations Pulse
                </span>
              </div>
              <span className="inline-flex items-center gap-1 text-xs text-sidebar-muted font-mono">
                <Clock className="size-3" /> Auto-sync
              </span>
            </div>

            {/* Interactive Hub Selector Tabs */}
            <div className="grid grid-cols-4 gap-1.5 p-1 rounded-xl bg-sidebar border border-sidebar-border/60 text-xs">
              {WORKSPACE_HUBS.map((hub, index) => {
                const isActive = index === activeTab;
                const Icon = hub.icon;
                return (
                  <div
                    key={hub.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveTab(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveTab(index);
                      }
                    }}
                    className={`relative flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg font-medium transition-all duration-200 cursor-pointer select-none ${
                      isActive
                        ? 'text-white bg-sidebar-accent shadow-xs'
                        : 'text-sidebar-muted hover:text-white hover:bg-sidebar-accent/50'
                    }`}
                  >
                    <Icon className="size-3.5 shrink-0" />
                    <span className="truncate">{hub.tabName.split(' ')[0]}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTabGlow"
                        className="absolute bottom-0 left-1 right-1 h-0.5 rounded-full bg-primary"
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Animated Hub Workflow Display */}
            <div className="min-h-[178px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentHub.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                  className="space-y-3"
                >
                  {/* Hub Banner */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex size-7 items-center justify-center rounded-md border ${currentHub.iconBox}`}
                      >
                        <HubIcon className="size-4" />
                      </span>
                      <span className="font-display text-sm font-semibold text-white">
                        {currentHub.hubName}
                      </span>
                    </div>
                    <span
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-mono font-medium ${currentHub.badgeColor}`}
                    >
                      {currentHub.badge}
                    </span>
                  </div>

                  {/* Real-time Work Activities */}
                  <div className="space-y-2 rounded-xl bg-sidebar/80 border border-sidebar-border/50 p-2.5 text-xs">
                    {currentHub.items.map((item) => (
                      <div
                        key={item.title}
                        className="flex items-center justify-between gap-2 py-1 px-1.5 rounded-md hover:bg-sidebar-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle2 className="size-3.5 text-success shrink-0" />
                          <div className="min-w-0">
                            <div className="font-medium text-white truncate">
                              {item.title}
                            </div>
                            <div className="text-sidebar-muted text-meta truncate">
                              {item.meta}
                            </div>
                          </div>
                        </div>
                        <span
                          className={`shrink-0 rounded-md border px-2 py-0.5 text-meta font-mono font-medium ${item.statusColor}`}
                        >
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Impact Summary */}
                  <div className="flex items-center gap-1.5 text-meta text-sidebar-muted font-mono pt-1">
                    <TrendingUp className="size-3 text-success shrink-0" />
                    <span className="truncate">{currentHub.highlight}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Cross-Platform Unified Banner */}
          <div className="flex items-center gap-3 rounded-xl border border-sidebar-border bg-sidebar-accent/30 p-3 text-xs text-sidebar-muted">
            <Layers className="size-4 text-sidebar-active shrink-0" />
            <span>
              <strong className="text-white font-medium">Single Source of Truth:</strong> One user identity, one shared database, zero disconnected spreadsheets.
            </span>
          </div>
        </div>

        {/* Bottom Trust & Compliance Bar */}
        <div className="pt-6 border-t border-sidebar-border flex items-center justify-between text-xs text-sidebar-muted">
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="size-3.5 text-success" />
            <span>Role-Based Access</span>
          </div>
          <div className="flex items-center gap-1.5">
            <KeyRound className="size-3.5 text-sidebar-active" />
            <span>Central SSO Ready</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Activity className="size-3.5 text-info" />
            <span>99.9% Uptime SLA</span>
          </div>
        </div>
      </aside>

      {/* Main sign-in card column */}
      <main className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-8">
        <div className="w-full max-w-[460px]">
          <div className="mb-8 md:hidden">
            <Logo className="h-9" />
          </div>
          <Outlet />
          <p className="mt-6 text-center text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} TeamOne Platform. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
}
